/* eslint-disable @typescript-eslint/no-explicit-any */
import { Icon, Render, theme as defaultTheme } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { Tooltip } from "@mui/material";
import { IcbtCommunication } from "api/types";
import { Slot } from "core/types/index";
import { isSameDay } from "date-fns";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { styled, css } from "styled-components";
import { Typography } from "components/typography";
import { useFilteredMeetingSlots } from "./useFilteredMeetingSlots";

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const DateContainer = styled.div`
  background: transparent;
  display: flex;
  align-items: center;
  position: relative;
  height: 30px;
  color: ${({ theme }) => defaultTheme.COLORS.GRAY_BLACK};
  font-size: ${({ theme }) => defaultTheme.FONT.SIZE.STANDARD};
  font-weight: ${({ theme }) => defaultTheme.FONT.WEIGHT.SEMIBOLD};
  width: 100%;
`;
const StatisticsContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${defaultTheme.COLORS.GRAY_DARK};
  font-size: ${defaultTheme.FONT.SIZE.SMALL};
  font-weight: ${defaultTheme.FONT.WEIGHT.REGULAR};
  gap: ${defaultTheme.PADDING.SM};
`;
const StatisticsItem = styled(StatisticsContainer)`
  gap: 2.5px;
  color: ${(props) => props.theme.COLORS.GRAY_DARK};

  & svg {
    width: ${(props) => props.theme.FONT.SIZE.STANDARD};
    height: ${(props) => props.theme.FONT.SIZE.STANDARD};
  }
`;

const StyledDay = styled.div<{ isToday: boolean }>`
  display: flex;
  flex: 1 0 50%;
  justify-content: flex-end;
  font-weight: ${(props) => (props.isToday ? 700 : 500)};
`;

const StyledDateWrapper = styled.div`
  display: flex;
  flex: 0 1 50%;
  justify-content: flex-start;
  padding-left: 4px;
`;

const StyledDate = styled.div<{ isToday: boolean }>`
  font-weight: 500;
  ${(props) =>
    props.isToday &&
    css`
      display: flex;
      justify-content: center;
      align-items: center;
      background: ${({ theme }) => defaultTheme.COLORS.GRAY_BLACK};
      color: ${({ theme }) => defaultTheme.COLORS.WHITE};
      padding: 5px;
      border-radius: 50%;
      height: 30px;
      width: 30px;
      font-weight: 700;
    `}
`;
interface Props {
  localizer: any;
  date: Date;
  slots: Slot[];
  feedbacks: IcbtCommunication[];
  label: string;
  icbtCommunicationFeaturesEnabled: boolean;
}

const WeekHeader = ({
  localizer,
  date,
  slots,
  feedbacks,
  icbtCommunicationFeaturesEnabled,
}: Props) => {
  const { t } = useTranslation();
  const improvedCalendarStatisticsEnabled = useFlag(
    "IMPROVED_CALENDAR_STATISTICS",
  );

  const filteredMeetingSlots = useFilteredMeetingSlots(slots);

  const slotsToUseForStatistics = improvedCalendarStatisticsEnabled
    ? filteredMeetingSlots
    : slots;

  return (
    <HeaderContainer>
      <DateContainer>
        <StyledDay isToday={isSameDay(new Date(), new Date(date))}>
          {localizer.format(date, "ddd")}
        </StyledDay>
        <StyledDateWrapper>
          <StyledDate isToday={isSameDay(new Date(), new Date(date))}>
            {localizer.format(date, "DD")}
          </StyledDate>
        </StyledDateWrapper>
      </DateContainer>
      <Render if={icbtCommunicationFeaturesEnabled}>
        <StatisticsContainer>
          <StatisticsItem>
            <Icon.Video style={{ color: defaultTheme.COLORS.GRAY_DARK }} />
            <Typography insensitive variant="body-small" color="GRAY_DARK">
              {slots.length === 0
                ? 0
                : t("COUNT_OF_TOTAL", {
                    count:
                      slotsToUseForStatistics.filter(
                        (status) => status === "BOOKED",
                      ).length ?? 0,
                    total: slotsToUseForStatistics.length,
                  })}
            </Typography>
          </StatisticsItem>
          <Tooltip title={t("ICBT_FEEDBACK_SCHEDULED_TOOLTIP")} placement="top">
            <StatisticsItem>
              <Icon.Feedback
                style={{ width: 16, color: defaultTheme.COLORS.GRAY_DARK }}
              />
              <Typography insensitive variant="body-small" color="GRAY_DARK">
                {feedbacks.length}
              </Typography>
            </StatisticsItem>
          </Tooltip>
        </StatisticsContainer>
      </Render>
    </HeaderContainer>
  );
};

export default memo(WeekHeader, (prevProps, nextProps) => {
  return prevProps.label === nextProps.label;
});
